import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import {
  bootstrapUser, linkPhone, getMyEvents,
  createCourt, updateCourt, getMyCourts, deleteCourt, generateSlots, getCustomerRecurrence
} from '../services/api';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const SPORT_OPTIONS = ['⚽ Fútbol', '🏓 Padel', '🏀 Básquet', '🎾 Tenis', '🚴 Ciclismo', '🏃 Running', '🎵 Social', '🍽️ Comer/Beber'];
const SPORT_COLORS = {
  '⚽ Fútbol': 'rgba(46,224,107,0.14)', '🏓 Padel': 'rgba(255,138,90,0.14)', '🏀 Básquet': 'rgba(90,160,255,0.14)',
  '🎾 Tenis': 'rgba(255,200,87,0.14)', '🚴 Ciclismo': 'rgba(255,255,255,0.07)', '🏃 Running': 'rgba(255,255,255,0.07)',
  '🎵 Social': 'rgba(200,120,255,0.14)', '🍽️ Comer/Beber': 'rgba(255,120,120,0.14)'
};

// No existe un emoji de Unicode para pádel (🏓 es ping-pong) — armamos el ícono
// real: paleta sólida con perforaciones y mango corto, como la de verdad.
const PADEL_HOLES = [
  [40, 24], [50, 22], [60, 24], [34, 32], [44, 32], [56, 32], [66, 32],
  [38, 42], [50, 42], [62, 42], [34, 52], [44, 52], [56, 52], [66, 52], [50, 60]
];
function PadelIcon({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <rect x="42" y="66" width="16" height="22" rx="7" fill="#2B2118" />
      <ellipse cx="50" cy="40" rx="34" ry="38" fill="#1C1C1C" stroke="#D4843C" strokeWidth="3" />
      {PADEL_HOLES.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.4" fill="#3D3D3D" />)}
      <circle cx="80" cy="74" r="9" fill="#D8E84A" />
      <circle cx="90" cy="86" r="9" fill="#D8E84A" />
    </svg>
  );
}
const GRID_DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const JS_DAY_TO_NAME = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// Si hay un rango de fechas acotado, calcula qué días de la semana caen
// adentro — para no dejar marcar horarios que jamás se van a generar.
// Devuelve null si no hay restricción útil (todos los días son válidos).
function computeValidDays(validFrom, validTo) {
  if (!validFrom || !validTo) return null;
  const from = new Date(validFrom + 'T00:00:00');
  const to = new Date(validTo + 'T00:00:00');
  if (isNaN(from) || isNaN(to) || from > to) return null;
  const days = new Set();
  const cursor = new Date(from);
  let count = 0;
  while (cursor <= to && count < 14) {
    days.add(JS_DAY_TO_NAME[cursor.getDay()]);
    cursor.setDate(cursor.getDate() + 1);
    count++;
  }
  return days.size >= 7 ? null : days;
}
const GRID_HOURS = Array.from({ length: 16 }, (_, i) => `${String(8 + i).padStart(2, '0')}:00`);

const emptyCourtForm = { name: '', typeLabel: SPORT_OPTIONS[0], level: 'Libre', slotDurationMinutes: '', capacity: '', pricePerSlot: '', paymentMethods: [], validFrom: '', validTo: '', autoPublish: false };
const LEVEL_OPTIONS = ['Libre', 'Normal', 'Medio', 'Avanzado'];
const PAYMENT_OPTIONS = ['Efectivo', 'Tarjeta de crédito', 'Tarjeta de débito', 'Transferencia'];

const card = { background: '#121A17', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 20, marginBottom: 16 };
const label = { fontSize: 12, color: '#9CA8A2', marginBottom: 6, display: 'block', fontWeight: 700, fontFamily: "'Nunito', sans-serif" };
const input = { width: '100%', background: '#0A0F0D', border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 12px', color: '#F4F7F5', fontSize: 14, marginBottom: 12, boxSizing: 'border-box', fontFamily: "'Nunito', sans-serif" };
const btnPrimary = { background: '#2EE06B', color: '#121A17', border: 'none', borderRadius: 999, padding: '10px 18px', fontSize: 13, fontFamily: "'Nunito', sans-serif", fontWeight: 800, cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#9CA8A2', border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 999, padding: '10px 18px', fontSize: 13, fontFamily: "'Nunito', sans-serif", fontWeight: 800, cursor: 'pointer' };
const btnDark = { background: '#F4F7F5', color: '#121A17', border: 'none', borderRadius: 999, padding: '10px 18px', fontSize: 13, fontFamily: "'Nunito', sans-serif", fontWeight: 800, cursor: 'pointer' };

const GLOBAL_STYLES = `
  .winal-fadein { animation: winalFadeIn .35s ease both; }
  @keyframes winalFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  .winal-btn:hover { filter: brightness(1.08); }
  .winal-cell { height: 17px; border-radius: 4px; background: #0A0F0D; cursor: pointer; transition: background .1s ease; }
  .winal-cell:hover { background: #DCE3E8; }
  .winal-cell.on { background: #2EE06B; }
  .winal-cell.on:hover { background: #27C75E; }
  .winal-court-card { transition: transform .15s ease, box-shadow .15s ease; }
  .winal-court-card:hover { transform: translateY(-3px); box-shadow: 0 12px 26px rgba(255,255,255,0.08); }
  .winal-overlay { position: fixed; inset: 0; background: rgba(14,22,31,0.5); display: flex; align-items: center; justify-content: center; z-index: 300; padding: 20px; }
  .winal-modal { background: #121A17; border-radius: 18px; width: 480px; max-width: 100%; max-height: 88vh; overflow-y: auto; padding: 26px; }
  .winal-dot { height: 5px; flex: 1; border-radius: 999px; background: rgba(255,255,255,0.08); }
  .winal-dot.done { background: #2EE06B; }
`;

export default function Courts() {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneInput, setPhoneInput] = useState('');

  const [courts, setCourts] = useState([]);
  const [venueEvents, setVenueEvents] = useState([]);
  const [recurrence, setRecurrence] = useState([]);
  const [courtForm, setCourtForm] = useState(emptyCourtForm);
  const [weeklySlots, setWeeklySlots] = useState([]);
  const [editingCourtId, setEditingCourtId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [msg, setMsg] = useState('');

  const refreshCourts = useCallback(async () => {
    try { setCourts(await getMyCourts()); } catch (e) { /* silencioso */ }
  }, []);

  const refreshVenueEvents = useCallback(async () => {
    try {
      const all = await getMyEvents();
      setVenueEvents(all.filter(e => e.courtId));
    } catch (e) { /* silencioso */ }
  }, []);

  const refreshRecurrence = useCallback(async () => {
    try { setRecurrence(await getCustomerRecurrence()); } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => {
    const init = async () => {
      if (!user?.email) return;
      try {
        const data = await bootstrapUser(user.email, user.displayName);
        setMe(data);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user]);

  useEffect(() => {
    if (me?.phone) { refreshCourts(); refreshVenueEvents(); refreshRecurrence(); }
  }, [me, refreshCourts, refreshVenueEvents, refreshRecurrence]);

  const handleLinkPhone = async () => {
    if (!phoneInput.trim()) return;
    try {
      const res = await linkPhone(phoneInput.trim());
      setMe({ ...me, phone: res.phone });
      setPhoneInput('');
    } catch (e) {
      setMsg('No pudimos vincular ese número.');
    }
  };

  const isSlotOn = (day, time) => weeklySlots.some(s => s.day === day && s.time === time);
  const toggleSlot = (day, time) => {
    if (isSlotOn(day, time)) {
      setWeeklySlots(weeklySlots.filter(s => !(s.day === day && s.time === time)));
    } else {
      setWeeklySlots([...weeklySlots, { day, time }]);
    }
  };

  const togglePayment = (method) => {
    if (method === 'Todos') {
      setCourtForm({ ...courtForm, paymentMethods: courtForm.paymentMethods.length === PAYMENT_OPTIONS.length ? [] : [...PAYMENT_OPTIONS] });
      return;
    }
    const has = courtForm.paymentMethods.includes(method);
    setCourtForm({
      ...courtForm,
      paymentMethods: has ? courtForm.paymentMethods.filter(m => m !== method) : [...courtForm.paymentMethods, method]
    });
  };

  const openCreateWizard = () => {
    setEditingCourtId(null);
    setCourtForm(emptyCourtForm);
    setWeeklySlots([]);
    setWizardStep(1);
    setWizardOpen(true);
    setMsg('');
  };

  const openEditWizard = (c) => {
    setEditingCourtId(c.id);
    setCourtForm({
      name: c.name, typeLabel: c.typeLabel, level: c.level || 'Libre', slotDurationMinutes: String(c.slotDurationMinutes),
      capacity: String(c.capacity), pricePerSlot: String(c.pricePerSlot || ''),
      paymentMethods: Array.isArray(c.paymentMethods) ? c.paymentMethods : [],
      validFrom: c.validFrom || '', validTo: c.validTo || '', autoPublish: !!c.autoPublish
    });
    setWeeklySlots(c.weeklySlots || []);
    setWizardStep(1);
    setWizardOpen(true);
    setMsg('');
  };

  const closeWizard = () => setWizardOpen(false);

  const goNextStep = () => {
    if (!courtForm.name.trim()) { setMsg('Poné un nombre para la cancha.'); return; }
    if (!courtForm.slotDurationMinutes || !courtForm.capacity) { setMsg('Completá duración y cupo del turno.'); return; }
    setMsg('');
    setWizardStep(2);
  };

  const handleSubmitCourt = async () => {
    setMsg('');
    if (weeklySlots.length === 0) { setMsg('Marcá al menos un horario en la grilla.'); return; }
    try {
      if (editingCourtId) {
        await updateCourt(editingCourtId, { ...courtForm, weeklySlots });
      } else {
        await createCourt({ ...courtForm, weeklySlots });
      }
      setWizardOpen(false);
      refreshCourts();
    } catch (e) {
      setMsg(e.response?.data?.error || 'No pudimos guardar la cancha.');
    }
  };

  const handleGenerateSlots = async (id) => {
    setMsg('');
    try {
      const res = await generateSlots(id);
      setMsg(`✅ Se publicaron ${res.created.length} turnos para esta semana — ya aparecen en "Eventos" y en WhatsApp para que la gente se sume.`);
      refreshVenueEvents();
    } catch (e) {
      setMsg(e.response?.data?.error || 'No pudimos generar los turnos.');
    }
  };

  const handleDeleteCourt = async (id) => {
    try {
      const res = await deleteCourt(id);
      setMsg(res.finishedEvents > 0 ? `Cancha borrada. Se finalizaron ${res.finishedEvents} turno(s) pendiente(s) de esa cancha.` : 'Cancha borrada.');
      refreshCourts();
      refreshVenueEvents();
    } catch (e) {
      setMsg('No pudimos borrar la cancha.');
    }
  };

  const handleToggleActive = async (c) => {
    try {
      await updateCourt(c.id, { active: !c.active });
      refreshCourts();
    } catch (e) {
      setMsg('No pudimos cambiar la disponibilidad.');
    }
  };

  const content = { marginLeft: isMobile ? 0 : 210, paddingTop: isMobile ? 56 : 0, padding: 28, minHeight: '100vh', background: '#0A0F0D', fontFamily: "'Nunito', sans-serif" };

  if (loading) {
    return (
      <>
        <Sidebar active="canchas" />
        <div style={content}><p style={{ color: '#9CA8A2' }}>Cargando...</p></div>
      </>
    );
  }

  if (me?.role !== 'negocio' && me?.role !== 'superadmin') {
    return (
      <>
        <Sidebar active="canchas" />
        <div style={content}>
          <div style={card}>
            <h3 style={{ color: '#F4F7F5', fontFamily: "'Nunito', sans-serif" }}>Esta sección es solo para cuentas Negocio</h3>
            <p style={{ color: '#9CA8A2', fontSize: 14 }}>Pedí una cuenta de Negocio desde la pantalla de Eventos.</p>
          </div>
        </div>
      </>
    );
  }

  if (me?.phone && (!me?.businessName || !me?.businessAddress)) {
    return (
      <>
        <Sidebar active="canchas" />
        <div style={content}>
          <div style={card}>
            <h3 style={{ color: '#F4F7F5', fontFamily: "'Nunito', sans-serif", fontWeight: 800, marginBottom: 8 }}>Antes de agregar canchas...</h3>
            <p style={{ color: '#9CA8A2', fontSize: 14 }}>Completá primero el nombre y la dirección en <strong>Datos del negocio</strong> — así no te lo volvemos a pedir por cada cancha.</p>
            <a href="/negocio-perfil" style={{ ...btnDark, display: 'inline-block', marginTop: 14, textDecoration: 'none' }}>Ir a Datos del negocio</a>
          </div>
        </div>
      </>
    );
  }

  const activeTurnos = venueEvents.filter(e => !e.finished);
  const jugadoresAnotados = activeTurnos.reduce((sum, e) => sum + (e.joinedPlayers || 0), 0);
  const recaudacionEstimada = activeTurnos.reduce((sum, e) => sum + (e.pricePerSlot || 0), 0);

  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <Sidebar active="canchas" />
      <div style={content}>
        <h1 style={{ color: '#F4F7F5', fontSize: 26, fontWeight: 800, marginBottom: 4, fontFamily: "'Nunito', sans-serif" }}>Mis canchas</h1>
        <p style={{ color: '#9CA8A2', fontSize: 13, marginBottom: 20 }}>{me?.businessName} — administración de canchas y turnos</p>

        {me?.phone && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ ...card, marginBottom: 0, flex: 1, minWidth: 140 }}>
              <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 24, fontWeight: 800, color: '#F4F7F5' }}>{courts.length}</div>
              <div style={{ fontSize: 12, color: '#9CA8A2', marginTop: 2 }}>Canchas activas</div>
            </div>
            <div style={{ ...card, marginBottom: 0, flex: 1, minWidth: 140 }}>
              <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 24, fontWeight: 800, color: '#F4F7F5' }}>{activeTurnos.length}</div>
              <div style={{ fontSize: 12, color: '#9CA8A2', marginTop: 2 }}>Turnos activos</div>
            </div>
            <div style={{ ...card, marginBottom: 0, flex: 1, minWidth: 140 }}>
              <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 24, fontWeight: 800, color: '#F4F7F5' }}>{jugadoresAnotados}</div>
              <div style={{ fontSize: 12, color: '#9CA8A2', marginTop: 2 }}>Jugadores anotados</div>
            </div>
            <div style={{ ...card, marginBottom: 0, flex: 1, minWidth: 140 }}>
              <div style={{ fontFamily: "'Nunito', sans-serif", fontSize: 24, fontWeight: 800, color: '#2EE06B' }}>${recaudacionEstimada.toLocaleString()}</div>
              <div style={{ fontSize: 12, color: '#9CA8A2', marginTop: 2 }}>Recaudación estimada</div>
            </div>
          </div>
        )}

        {msg && !wizardOpen && <div style={{ background: 'rgba(46,224,107,0.14)', color: '#5EE08A', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{msg}</div>}

        {!me?.phone ? (
          <div style={card}>
            <h3 style={{ color: '#F4F7F5', fontFamily: "'Nunito', sans-serif", fontWeight: 800, marginBottom: 8 }}>Vinculá tu WhatsApp</h3>
            <p style={{ color: '#9CA8A2', fontSize: 13, marginBottom: 12 }}>Necesario para poder publicar turnos.</p>
            <input style={input} placeholder="+54 9 11 ..." value={phoneInput} onChange={e => setPhoneInput(e.target.value)} />
            <button className="winal-btn" style={btnDark} onClick={handleLinkPhone}>Vincular</button>
          </div>
        ) : (
          <>
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <h3 style={{ color: '#F4F7F5', fontFamily: "'Nunito', sans-serif", fontWeight: 800 }}>Tus canchas</h3>
                  <p style={{ color: '#9CA8A2', fontSize: 12 }}>"Publicar semana" crea los próximos 7 días de turnos para que la gente se sume — por la web o por WhatsApp.</p>
                </div>
                <button className="winal-btn" style={btnDark} onClick={openCreateWizard}>+ Crear cancha</button>
              </div>

              {courts.length === 0 ? (
                <div style={{ border: '2px dashed rgba(255,255,255,0.14)', borderRadius: 14, padding: '40px 20px', textAlign: 'center', marginTop: 14 }}>
                  <div style={{ fontSize: 30, marginBottom: 10 }}>🏟️</div>
                  <p style={{ color: '#9CA8A2', fontSize: 13, marginBottom: 14 }}>Todavía no tenés canchas configuradas.</p>
                  <button className="winal-btn" style={btnPrimary} onClick={openCreateWizard}>+ Crear mi primera cancha</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12, marginTop: 14 }}>
                  {courts.map(c => {
                    const slotCount = (c.weeklySlots || []).length;
                    return (
                    <div key={c.id} className="winal-fadein winal-court-card" style={{ background: '#0A0F0D', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 14 }}>
                      <div style={{ height: 56, borderRadius: 10, background: SPORT_COLORS[c.typeLabel] || 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, marginBottom: 10 }}>
                        {c.typeLabel === '🏓 Padel' ? <PadelIcon size={30} /> : c.typeLabel.split(' ')[0]}
                      </div>
                      <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: '#F4F7F5', fontSize: 14 }}>{c.name}</div>
                      <div style={{ color: '#9CA8A2', fontSize: 11.5, marginBottom: 6 }}>{c.capacity} personas · {c.slotDurationMinutes} min · ${c.pricePerSlot || 0}</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        <span style={{ display: 'inline-flex', fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: c.active ? 'rgba(46,224,107,0.14)' : 'rgba(255,255,255,0.06)', color: c.active ? '#5EE08A' : '#9CA8A2' }}>
                          {c.active ? 'ACTIVA' : 'DESACTIVADA'}
                        </span>
                        <span style={{ display: 'inline-flex', fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: slotCount > 0 ? '#0A0F0D' : 'rgba(239,68,68,0.14)', color: slotCount > 0 ? '#9CA8A2' : '#FF8A8A', border: slotCount > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                          {slotCount > 0 ? `🗓️ ${slotCount} horario${slotCount !== 1 ? 's' : ''}/semana` : '⚠️ Sin horarios — editá para agregar'}
                        </span>
                        {c.autoPublish && (
                          <span style={{ display: 'inline-flex', fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'rgba(46,224,107,0.14)', color: '#1F9E4D' }}>🔁 Auto-publica</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="winal-btn" style={{ ...btnPrimary, padding: '7px 12px', fontSize: 11.5 }} onClick={() => handleGenerateSlots(c.id)} disabled={slotCount === 0}>Publicar semana</button>
                        <button className="winal-btn" style={{ ...btnSecondary, padding: '7px 12px', fontSize: 11.5 }} onClick={() => openEditWizard(c)}>Editar</button>
                        <button className="winal-btn" style={{ ...btnSecondary, padding: '7px 12px', fontSize: 11.5 }} onClick={() => handleToggleActive(c)}>{c.active ? 'Pausar' : 'Activar'}</button>
                        <button className="winal-btn" style={{ ...btnSecondary, padding: '7px 12px', fontSize: 11.5 }} onClick={() => handleDeleteCourt(c.id)}>Borrar</button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={card}>
              <h3 style={{ color: '#F4F7F5', fontFamily: "'Nunito', sans-serif", fontWeight: 800, marginBottom: 6 }}>Clientes frecuentes</h3>
              <p style={{ color: '#9CA8A2', fontSize: 12, marginBottom: 14 }}>Cuántas veces se anotó cada teléfono en tus turnos.</p>
              {recurrence.length === 0 ? (
                <p style={{ color: '#9CA8A2', fontSize: 13 }}>Todavía no hay datos suficientes.</p>
              ) : (
                recurrence.slice(0, 15).map(r => (
                  <div key={r.phone} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ fontSize: 13, color: '#F4F7F5' }}>{r.name || 'Sin nombre'} — {r.phone}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#2EE06B' }}>{r.visits} visita{r.visits !== 1 ? 's' : ''}</span>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {wizardOpen && (
        <div className="winal-overlay" onClick={closeWizard}>
          <div className="winal-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ color: '#F4F7F5', fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 18 }}>
                {editingCourtId ? 'Editar cancha' : (wizardStep === 1 ? 'Crear cancha · paso 1 de 2' : '¿Cuándo está disponible? · paso 2 de 2')}
              </h3>
              <button onClick={closeWizard} style={{ background: 'transparent', border: 'none', color: '#9CA8A2', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
              <div className={`winal-dot ${wizardStep >= 1 ? 'done' : ''}`} />
              <div className={`winal-dot ${wizardStep >= 2 ? 'done' : ''}`} />
            </div>

            {msg && <div style={{ background: 'rgba(239,68,68,0.14)', color: '#FF8A8A', borderRadius: 10, padding: '9px 12px', fontSize: 12.5, marginBottom: 14 }}>{msg}</div>}

            {wizardStep === 1 && (
              <>
                <label style={label}>Nombre de la cancha</label>
                <input style={input} placeholder="Ej: Cancha 1" value={courtForm.name} onChange={e => setCourtForm({ ...courtForm, name: e.target.value })} />
                <label style={label}>Deporte</label>
                <select style={input} value={courtForm.typeLabel} onChange={e => setCourtForm({ ...courtForm, typeLabel: e.target.value })}>
                  {SPORT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <label style={label}>Nivel de juego</label>
                <select style={input} value={courtForm.level} onChange={e => setCourtForm({ ...courtForm, level: e.target.value })}>
                  {LEVEL_OPTIONS.map(o => <option key={o} value={o}>{o === 'Libre' ? 'Libre (cualquier nivel)' : o}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={label}>Personas por turno</label>
                    <input style={input} type="number" placeholder="4" value={courtForm.capacity} onChange={e => setCourtForm({ ...courtForm, capacity: e.target.value })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={label}>Duración (min)</label>
                    <input style={input} type="number" placeholder="60" value={courtForm.slotDurationMinutes} onChange={e => setCourtForm({ ...courtForm, slotDurationMinutes: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={label}>Precio del turno ($)</label>
                    <input style={input} type="number" placeholder="12000" value={courtForm.pricePerSlot} onChange={e => setCourtForm({ ...courtForm, pricePerSlot: e.target.value })} />
                  </div>
                </div>
                <label style={label}>Forma de pago — elegí una o más</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                  {[...PAYMENT_OPTIONS, 'Todos'].map(method => {
                    const checked = method === 'Todos' ? courtForm.paymentMethods.length === PAYMENT_OPTIONS.length : courtForm.paymentMethods.includes(method);
                    return (
                      <label key={method} style={{
                        display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontFamily: "'Nunito', sans-serif",
                        padding: '7px 12px', borderRadius: 999, cursor: 'pointer',
                        background: checked ? 'rgba(46,224,107,0.14)' : '#0A0F0D',
                        border: `1.5px solid ${checked ? '#2EE06B' : 'rgba(255,255,255,0.08)'}`,
                        color: checked ? '#1F9E4D' : '#9CA8A2', fontWeight: 700
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => togglePayment(method)} style={{ accentColor: '#2EE06B' }} />
                        {method}
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {wizardStep === 2 && (
              <>
                <label style={label}>Vigencia (opcional)</label>
                <p style={{ fontSize: 11.5, color: '#9CA8A2', marginTop: -2, marginBottom: 10 }}>
                  Dejá ambas vacías para que la disponibilidad sea permanente. Si querés abrir la cancha un solo día, poné la misma fecha en las dos.
                </p>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ ...label, fontWeight: 400 }}>Desde</label>
                    <input style={input} type="date" value={courtForm.validFrom} onChange={e => setCourtForm({ ...courtForm, validFrom: e.target.value })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ ...label, fontWeight: 400 }}>Hasta</label>
                    <input style={input} type="date" value={courtForm.validTo} onChange={e => setCourtForm({ ...courtForm, validTo: e.target.value })} />
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#0A0F0D', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                  <input type="checkbox" checked={courtForm.autoPublish} onChange={e => setCourtForm({ ...courtForm, autoPublish: e.target.checked })} style={{ marginTop: 2, accentColor: '#2EE06B' }} />
                  <div>
                    <label style={{ fontSize: 13, fontWeight: 700, color: '#F4F7F5', cursor: 'pointer' }} onClick={() => setCourtForm({ ...courtForm, autoPublish: !courtForm.autoPublish })}>Publicar automáticamente cada día</label>
                    <p style={{ fontSize: 11.5, color: '#9CA8A2', margin: '3px 0 0' }}>
                      Si está apagado (recomendado al principio), vos decidís cuándo tocar "Publicar semana". Si lo activás, Winal mantiene la semana siempre llena sin que tengas que entrar.
                    </p>
                  </div>
                </div>

                <p style={{ fontSize: 12.5, color: '#9CA8A2', marginBottom: 14 }}>¿Qué días y horarios está libre? Tocá las celdas — generamos los turnos solos cada semana.</p>
                {(() => {
                  const validDays = computeValidDays(courtForm.validFrom, courtForm.validTo);
                  const isDayBlocked = (d) => validDays !== null && !validDays.has(d);
                  return (
                    <>
                      {validDays !== null && (
                        <p style={{ fontSize: 11.5, color: '#FFC857', background: 'rgba(255,200,87,0.18)', borderRadius: 8, padding: '7px 10px', marginBottom: 10 }}>
                          ⚠️ Con esa fecha, solo {validDays.size === 1 ? 'este día de la semana' : 'estos días'} puede tener turnos: <strong>{[...validDays].join(', ')}</strong>. Los demás están bloqueados en la grilla.
                        </p>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '36px repeat(7, 1fr)', gap: 3, marginBottom: 8 }}>
                        <div></div>
                        {GRID_DAYS.map(d => (
                          <div key={d} style={{ fontSize: 10, fontWeight: 700, color: isDayBlocked(d) ? '#C2CAD1' : '#9CA8A2', textAlign: 'center' }}>{d.slice(0, 2)}</div>
                        ))}
                        {GRID_HOURS.map(h => (
                          <React.Fragment key={h}>
                            <div style={{ fontSize: 9.5, color: '#9CA8A2', display: 'flex', alignItems: 'center' }}>{h}</div>
                            {GRID_DAYS.map(d => {
                              const blocked = isDayBlocked(d);
                              const on = isSlotOn(d, h);
                              return (
                                <div
                                  key={d + h}
                                  className={`winal-cell ${on && !blocked ? 'on' : ''}`}
                                  style={blocked ? { background: on ? '#FFE2B0' : '#F4F4F4', cursor: 'not-allowed', opacity: 0.6 } : undefined}
                                  title={blocked ? `${d} queda fuera del rango de fechas elegido` : undefined}
                                  onClick={() => { if (blocked && !on) return; toggleSlot(d, h); }}
                                />
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                    </>
                  );
                })()}
                <p style={{ fontSize: 11.5, color: '#9CA8A2' }}>{weeklySlots.length} horario{weeklySlots.length !== 1 ? 's' : ''} marcado{weeklySlots.length !== 1 ? 's' : ''}.</p>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              {wizardStep === 2 ? (
                <button className="winal-btn" style={btnSecondary} onClick={() => setWizardStep(1)}>← Atrás</button>
              ) : <div />}
              {wizardStep === 1 ? (
                <button className="winal-btn" style={btnDark} onClick={goNextStep}>Siguiente →</button>
              ) : (
                <button className="winal-btn" style={btnPrimary} onClick={handleSubmitCourt}>{editingCourtId ? 'Guardar cambios' : 'Crear cancha'}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}